import type { CrmOnboardingStatus } from '../../types/crmOnboarding';
import {
  clubCoverageLine,
  clubLinkLine,
  sortClubsForOnboarding,
  type ClubCoverageTone,
} from '../../lib/crmOnboarding/status';

// The CRM health panel: per club, registered · coverage floor · last pull ·
// linked account. Presentational only, so it is server-render tested.
//
// ⚠️ A NEVER-PULLED CLUB IS TONED PENDING (sky), NEVER RED. It is the mechanism
// working: the next scheduled run gives it its automatic first pull.

const TONE: Record<ClubCoverageTone, string> = {
  ok: 'text-slate-800',
  pending: 'text-sky-800',
  attention: 'text-amber-800',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export function ClubHealthTable({ status }: { status: CrmOnboardingStatus }) {
  const clubs = sortClubsForOnboarding(status.clubs);
  const run = status.lastScheduledRun;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-700" data-testid="last-run">
        {run === null
          ? 'No scheduled pull has run for this brand yet.'
          : `Last scheduled pull started ${fmtDate(run.startedAt)}${
              run.finishedAt ? `, finished ${fmtDate(run.finishedAt)}` : ' and has not finished'
            } · ${run.clubsWritten ?? "an unknown number of"} club(s) written${
              (run.failureCount ?? 0) > 0 ? ` · ${run.failureCount} failure(s)` : ''
            }${run.error ? ` · error: ${run.error}` : ''}.`}{' '}
        Each run gives at most {status.maxFirstPullsPerRun} never-pulled clubs their first pull; more than that
        takes several runs.
      </p>
      {clubs.length === 0 ? (
        <p className="text-sm text-slate-600">No clubs are registered for this brand.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Club</th>
                <th className="px-3 py-2 font-medium">Coverage</th>
                <th className="px-3 py-2 font-medium">Data held</th>
                <th className="px-3 py-2 font-medium">Last reached by a pull</th>
                <th className="px-3 py-2 font-medium">Kyck account</th>
              </tr>
            </thead>
            <tbody>
              {clubs.map((c) => {
                const cov = clubCoverageLine(c, status.firstPullDays);
                const link = clubLinkLine(c);
                return (
                  <tr key={c.companyId} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{c.name ?? '(no name)'}</div>
                      <div className="font-mono text-xs text-slate-500">{c.companyId}</div>
                    </td>
                    <td className={`px-3 py-2 ${TONE[cov.tone]}`} data-tone={cov.tone}>
                      {cov.text}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {c.coverageFrom ? `${c.coverageFrom} → ${c.dataThrough ?? '—'}` : 'Nothing held yet'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{fmtDate(c.deliveryThrough)}</td>
                    <td className={`px-3 py-2 ${TONE[link.tone]}`}>{link.text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
